import React, { useEffect, useRef, useCallback } from "react";
import { Spine } from "pixi-spine";
import { Container, BitmapText } from "@pixi/react";
import { getAsset } from "../../assets";
import { useSelector, useDispatch } from "react-redux";
import {
  setFreeSpinOutroAnimationCompleted,
  setFreeSpinOutroAnimationStarted,
} from "../../State/Slices/animationsSlice";

import { Assets } from "@pixi/assets";
import { playSound, stopSound } from "../../State/Slices/soundsSlice";
import { SOUND_NAME } from "../../sounds";
import { formatToCustomLocale } from "../../utilities";
import { setSimulationMode } from "../../State/Slices/settingsSlice";
import {
  resetRoundIndex,
  resetFreeSpinWins,
} from "../../State/Slices/gameSettingsSlice";
import {
  setFreeSpinActive,
  setFreeSpinMode,
} from "../../State/Slices/freeSpinsSlice";

const FreeSpinOutro = ({ animationPath, x, y, zIndex = 0, symbolSize }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const spineAnimRef = useRef(null);
  const isAnimationLoaded = useRef(false);
  const isAnimationPlaying = useRef(false);

  const { freeSpinOutroAnimationCompleted, triggerFreeSpinOutroOutPhase } =
    useSelector((state) => state.animations);
  const { status } = useSelector((state) => state.assets);
  const { totalFreeSpinWin } = useSelector((state) => state.game);
  const { backgroundMusicEnabled } = useSelector((state) => state.sound);
  const playAnimationSequence = useCallback(
    (spineAnim, animations, index = 0) => {
      if (
        !spineAnim ||
        index >= animations.length ||
        freeSpinOutroAnimationCompleted
      ) {
        return;
      }

      const { name } = animations[index];
      const isLoop = name.includes("Loop");
      const isInPhase = name.includes("In");
      const isOutPhase = name.includes("Out");

      spineAnim.state.clearListeners();

      if (isInPhase) {
        spineAnim.state.setAnimation(0, name, false);
        spineAnim.state.addListener({
          complete: () => {
            playAnimationSequence(spineAnim, animations, index + 1);
          },
        });
      } else if (isLoop) {
        if (!freeSpinOutroAnimationCompleted) {
          spineAnim.state.setAnimation(0, name, true);
          isAnimationPlaying.current = true;
          console.log(`Playing loop animation: ${name}`);
        }
      } else if (isOutPhase && triggerFreeSpinOutroOutPhase) {
        spineAnim.state.setAnimation(0, name, false);
        spineAnim.state.addListener({
          complete: () => {
            dispatch(stopSound(SOUND_NAME.freeSpinOutro));
            dispatch(setFreeSpinOutroAnimationStarted(false));
            dispatch(setFreeSpinOutroAnimationCompleted(true));
            dispatch(stopSound(SOUND_NAME.freeSpinMusic));

            if (backgroundMusicEnabled) {
              dispatch(playSound(SOUND_NAME.backgroundMusic));
            }

            dispatch(setFreeSpinMode(false));
            dispatch(setSimulationMode(false));
            dispatch(setFreeSpinActive(false));
            dispatch(resetRoundIndex());
            dispatch(resetFreeSpinWins());
            isAnimationPlaying.current = false;
          },
        });
      }
    },
    [
      dispatch,
      freeSpinOutroAnimationCompleted,
      triggerFreeSpinOutroOutPhase,
      backgroundMusicEnabled,
    ]
  );

  const playStageAnimation = useCallback(
    (spineAnim) => {
      if (!spineAnim?.spineData) {
        console.warn("spineAnim is not initialized or spineData is missing.");
        return;
      }

      const animations = spineAnim.spineData.animations.map((anim) => ({
        name: anim.name,
        duration: anim.duration,
      }));
      playAnimationSequence(spineAnim, animations);
    },
    [playAnimationSequence]
  );

  useEffect(() => {
    if (freeSpinOutroAnimationCompleted) return;

    const loadSpineAnimation = async () => {
      try {
        let spineResource = getAsset(status, animationPath);
        if (!spineResource?.spineData) {
          console.warn(
            "Spine resource or data is missing! Attempting to load asset..."
          );
          await Assets.load(animationPath);
          const newSpineResource = getAsset(status, animationPath);
          if (!newSpineResource?.spineData) {
            console.error(
              "Failed to load spine resource after attempting to load it."
            );
            return;
          }
          spineResource = newSpineResource;
        }

        const spineAnim = new Spine(spineResource.spineData);
        spineAnimRef.current = spineAnim;
        isAnimationLoaded.current = true;

        spineAnim.position.set(0, 0);
        spineAnim.width = symbolSize * 7;
        spineAnim.height = symbolSize * 7;

        if (containerRef.current) {
          containerRef.current.addChild(spineAnim);
          playStageAnimation(spineAnim);
        }
      } catch (error) {
        console.error("Error loading Spine animation:", error);
      }
    };

    loadSpineAnimation();
    dispatch(stopSound(SOUND_NAME.freeSpinMusic));
    dispatch(playSound(SOUND_NAME.freeSpinIntro));
    dispatch(setFreeSpinOutroAnimationStarted(true));

    return () => {
      console.log("Cleaning up FreeSpinOutro component...");

      if (spineAnimRef.current) {
        const spineAnim = spineAnimRef.current;
        if (spineAnim.state) {
          spineAnim.state.clearListeners();
        }
      } else {
        console.warn("Skipping cleanup: Animation is still playing.");
      }
    };
  }, [animationPath, dispatch, status, freeSpinOutroAnimationCompleted]);

  useEffect(() => {
    if (
      triggerFreeSpinOutroOutPhase &&
      spineAnimRef.current &&
      isAnimationLoaded.current &&
      !freeSpinOutroAnimationCompleted
    ) {
      const spineAnim = spineAnimRef.current;
      if (!spineAnim?.spineData?.animations) return;

      const animations = spineAnim.spineData.animations.map((anim) => ({
        name: anim.name,
        duration: anim.duration,
      }));
      const outPhaseIndex = animations.findIndex((anim) =>
        anim.name.includes("Out")
      );
      if (outPhaseIndex !== -1) {
        playAnimationSequence(spineAnim, animations, outPhaseIndex);
      }
    }
  }, [
    triggerFreeSpinOutroOutPhase,
    freeSpinOutroAnimationCompleted,
    playAnimationSequence,
  ]);

  useEffect(() => {
    if (spineAnimRef.current) {
      const spineAnim = spineAnimRef.current;

      spineAnim.width = symbolSize * 7;
      spineAnim.height = symbolSize * 7;
    }
  }, [symbolSize]);

  return (
    <>
      <Container
        ref={containerRef}
        x={x}
        y={y}
        zIndex={zIndex}
        name="Spine Outro Animation"
        sortChildren={true}
      />
      {!freeSpinOutroAnimationCompleted && (
        <BitmapText
          x={x}
          y={y + symbolSize / 2 + 16}
          text={`${formatToCustomLocale(totalFreeSpinWin)}`}
          anchor={0.5}
          style={{
            fontName: `jackpot-diamond`,
            fontSize: 48,
            fill: "#ffffff",
            fontWeight: "bold",
            align: "center",
          }}
        />
      )}
    </>
  );
};

export default FreeSpinOutro;
